import { type FetchResult, useMutation } from '@apollo/client';
import { Button, Container, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import type React from 'react';
import { useState } from 'react';
import useStyles from 'src/AkomiLoginPage/ResetPasswordPage/ResetPasswordPageStyles';
import createResetPasswordRequest from 'src/api/resetPassword/create';
import Logo from 'src/components/logo';
import * as Yup from 'yup';

interface ResetPasswordFormValues {
  email: string;
}

interface ResetPasswordRequestResponse {
  createPasswordResetRequest: {
    resetRequest: string;
  };
}

interface ResetPasswordRequestVariables {
  email: string;
  baseUrl: string;
}

const ResetPasswordRequestPage: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [sendResetPasswordRequest] = useMutation<ResetPasswordRequestResponse, ResetPasswordRequestVariables>(
    createResetPasswordRequest,
  );
  const [success, setSuccess] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string>('');

  const formik = useFormik<ResetPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email format').required('This field is required'),
    }),
    onSubmit: async (
      values: ResetPasswordFormValues,
      _helpers: FormikHelpers<ResetPasswordFormValues>,
    ): Promise<void> => {
      const { email } = values;
      setResetError('');
      sendResetPasswordRequest({
        variables: {
          email: email,
          baseUrl: `${window.location.origin}/reset-password/`,
        },
      })
        .then((resp: FetchResult<ResetPasswordRequestResponse>) => {
          if (!resp.errors) {
            setSuccess(true);
          }
        })
        .catch((e: Error) => {
          setResetError(e.message);
        });
    },
  });

  const goHome = (): void => {
    navigate({ to: '/' });
  };

  return (
    <div className={classes.akomiPage}>
      <div className={classes.akomiLogo}>
        <Logo />
      </div>
      <div className={classes.resetPasswordForm}>
        <Container className={classes.container} component="main">
          {success ? (
            <div className={classes.successContainer}>
              <Typography component="h1" variant="h1" className={classes.title}>
                Request Successful
              </Typography>
              <Typography component="p" className={classes.p}>
                You should receive an email with password reset instructions shortly.
              </Typography>
              <Button variant="contained" color="primary" className={classes.submitButton} onClick={goHome}>
                Login
              </Button>
            </div>
          ) : (
            <form className={classes.form} onSubmit={formik.handleSubmit}>
              <Typography component="h1" variant="h1" className={classes.title}>
                Reset Password
              </Typography>
              <Typography component="p" className={classes.p}>
                {
                  "Please enter your account's email address. A message will be sent there with instructions to reset your password."
                }
              </Typography>
              <div className={classes.formElements}>
                <TextField
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  error={formik.touched.email && !!formik.errors.email}
                  helperText={formik.touched.email && formik.errors.email}
                  {...formik.getFieldProps('email')}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!!formik.errors.email}
                  className={classes.submitButton}
                >
                  Reset Password
                </Button>
              </div>
              {resetError && <Typography color="error">{resetError}</Typography>}
              <Typography component="p" className={classes.backToLogin}>
                <Button onClick={goHome} color="primary">
                  Back to Login
                </Button>
              </Typography>
            </form>
          )}
        </Container>
      </div>
    </div>
  );
};

export default ResetPasswordRequestPage;
